import { defineStore } from 'pinia'
import { useActivityStore } from './activity'
import { TOOL_PRESENTATION } from '~~/shared/tools/tool'
import { toolRunFailureText } from '~/utils/toolRunMessages'
import { documentReviewPath } from '~/utils/docPanes'
import type { ToolRunError } from '~~/shared/tools/tool'
import { parseUsageLimitResponse } from '~~/shared/usage/usageLimit'
import { recordUsageLimitActivity } from '~/composables/useUsageLimit'
import type { ActivityRunRow } from '~~/shared/activity/activityFeed'
import type { ActivityAction, ActivityKind, ActivityState, ActivityTask } from '~~/shared/activity/types'

/**
 * Contract reviews, sheet extractions, OCR reviews and tool runs in the Activity Center (#56, #70).
 *
 * ── ONE PRODUCER FOR FOUR KINDS ──────────────────────────────────────────────────────────────────
 *
 * Every other producer owns one kind because each has its own transport and its own domain state.
 * These four share BOTH: they arrive on the same `runs` array of the one activity poll, already
 * reduced to a phase by `shared/runs/phase.ts`. Four stores would be four copies of the same
 * fifteen lines differing only in a label.
 *
 * ── TOOL RUNS ARE HERE NOW, AND WHAT THAT CHANGED (#70) ──────────────────────────────────────────
 *
 * They used to be published by `stores/toolRuns.ts`, which ran its OWN 4s timer against
 * `GET /api/tools/runs` while this feed was already receiving the same runs and dropping them to
 * avoid double rows. Two pollers over one table is the thing the Activity Center exists to end, so
 * that store and that endpoint are gone.
 *
 * The two reads never saw the same rows, and the one kept is this one: owner-scoped as well as
 * org-scoped, inside the 24h window, limited to the phases that still want attention, and filtered
 * by `activity_center_dismissed_at`. So a colleague's conversion no longer appears in your bell, a
 * settled receipt no longer lingers forever — and a failed conversion is DISMISSABLE, which it
 * structurally could not be from the old producer: nothing there could reach that column.
 *
 * ── THE PANEL STILL LEARNS NOTHING ───────────────────────────────────────────────────────────────
 *
 * `detail` and every label are resolved here, through `t()`, before publishing. The server sent a
 * document name and an untranslated phase; the sentence a user reads is made in this file.
 */

/**
 * Feed kind → the panel's task family and the label its title is built from.
 *
 * `tool-run` has no `titleKey`: its title is the TOOL's label, which differs per row and lives in
 * `TOOL_PRESENTATION` (Spanish copy in code, deliberately not in the i18n bundle), so `titleFor`
 * resolves it from the row's `toolId` instead.
 */
const KIND_MAP = {
  'contract-review': { kind: 'contract' as ActivityKind, titleKey: 'activity.run.contractReview' },
  'sheet-extraction': { kind: 'sheet' as ActivityKind, titleKey: 'activity.run.sheetExtraction' },
  'ocr-review': { kind: 'ocr' as ActivityKind, titleKey: 'activity.run.ocrReview' },
  'tool-run': { kind: 'tool' as ActivityKind, titleKey: null },
} as const

type HandledKind = keyof typeof KIND_MAP

function isHandled(row: ActivityRunRow): row is ActivityRunRow & { kind: HandledKind } {
  return row.kind in KIND_MAP
}

/**
 * The typed usage-limit block on a failed run, or `null` (MAR-1009). A sheet run the gateway refused
 * stores it as `sheet_runs.error.usageLimit` (`SHEET_RUN_FAILURES.usageLimit`); no other kind here
 * carries one yet, and a row without it is an ordinary failure.
 */
function usageLimitOfRun(row: ActivityRunRow) {
  if (row.phase !== 'failed' || !row.error || typeof row.error !== 'object') return null
  return parseUsageLimitResponse((row.error as { usageLimit?: unknown }).usageLimit)
}

export const useActivityRunsStore = defineStore('activityRuns', () => {
  const { t } = useI18n()
  const { apiFetch } = useApi()
  const activity = useActivityStore()

  const rows = ref<ActivityRunRow[]>([])

  /**
   * `waiting_on_user` and `failed` are both `attention`, for the same reason and not the same one.
   *
   * A failure STOPPED and wants a decision. A `waiting_on_user` row never started and wants a
   * decision — an OCR read a person has to accept, a quota period to roll over. Neither is `running`
   * (nothing is working on them, and a spinner would claim otherwise) and neither is `done`. That
   * they sort together above everything is the whole point of the state.
   */
  function toState(row: ActivityRunRow): ActivityState {
    switch (row.phase) {
      case 'queued': return 'queued'
      case 'running': return 'running'
      case 'failed':
      case 'waiting_on_user': return 'attention'
      // `succeeded` and `cancelled` are receipts. The feed filters them out, so this is only
      // reachable for a row still being watched — it settles rather than lingering as work.
      default: return 'done'
    }
  }

  /**
   * One line, ALREADY LOCALIZED.
   *
   * Progress is shown only where the table actually counts something; a contract review reports
   * null positions and gets the document name instead, which is more use than a fabricated 0/0.
   */
  function detailFor(row: ActivityRunRow): string {
    if (row.phase === 'failed') {
      // A tool run's `error` is a `{ code, message }` payload whose `message` is an English operator
      // diagnostic naming the internal tool id — never printable (MAR-491). The sentence comes from
      // the code, through the same helper the chat conversion card uses.
      if (row.kind === 'tool-run') {
        return toolRunFailureText(t, row.error as ToolRunError | null, row.title)
      }
      return typeof row.error === 'string' && row.error.trim()
        ? row.error.trim()
        : t('activity.run.failed')
    }
    if (row.phase === 'waiting_on_user') return t('activity.run.waiting')
    if (row.total && row.done !== null) {
      return t('activity.run.progress', { done: row.done, total: row.total })
    }
    return row.title ?? ''
  }

  /** Where a row points. Each kind's `targetId` means its own thing — see the server projection. */
  function linkFor(row: ActivityRunRow & { kind: HandledKind }): string | undefined {
    // A review is read on its document (MAR-1097); `targetId` is that document (the server projection).
    if (row.kind === 'contract-review') return row.targetId ? documentReviewPath({ itemId: row.targetId, reviewId: row.id }) : undefined
    if (row.kind === 'sheet-extraction') return row.targetId ? `/app/sheets/${row.targetId}` : undefined
    // OCR reads and tool runs both point at a document. A tool run only HAS one once it succeeded,
    // and the feed filters settled rows out — so in practice this is null for a conversion, which is
    // why nothing here tries to offer "Abrir documento" on a run that produced nothing.
    return row.targetId ? `/app/docs/${row.targetId}` : undefined
  }

  /**
   * The title — a kind's label, except for a tool run, which is a per-row one.
   *
   * `TOOL_PRESENTATION` is the roster in code (`shared/tools/tool.ts`), so an unknown id can only
   * mean a tool that was removed while a run of it was still in the window: fall back to the source
   * document's name rather than render `undefined`.
   */
  function titleFor(row: ActivityRunRow & { kind: HandledKind }): string {
    const { titleKey } = KIND_MAP[row.kind]
    if (titleKey) return t(titleKey)
    const tool = row.toolId ? TOOL_PRESENTATION[row.toolId] : undefined
    return tool?.label ?? row.title ?? ''
  }

  /**
   * What a row offers.
   *
   * A FAILED row must be dismissable (#98). Until now these three kinds offered only `open`, and
   * being `attention` they are skipped by "Limpiar" by construction — so a failure here was
   * permanent, the one state in the panel with no way out. The affordance is the same durable one
   * workflow and indexing rows already have: the producer calls `POST /api/activity/dismiss`, the
   * server stamps `activity_center_dismissed_at`, and discovery stops returning the row.
   *
   * `failed` and NOT `waiting_on_user`, though both are `attention`. A failure has stopped and owes
   * the user nothing but the choice to stop looking at it; a `waiting_on_user` row is a door held
   * open — an OCR read waiting to be accepted — and hiding it would discard work the user asked for.
   * That is also why `isSettled()` stays `state === 'done'`: widening it would make "Limpiar" sweep
   * away pending decisions, and would count failures in the "listos hoy" receipt.
   */
  function actionsFor(row: ActivityRunRow & { kind: HandledKind }): ActivityAction[] | undefined {
    const to = linkFor(row)
    const openLabel = row.kind === 'tool-run' ? t('activity.tool.openDocument') : t('activity.run.open')
    const open: ActivityAction[] = to
      ? [{ id: 'open', label: openLabel, kind: 'link', to, variant: 'primary' }]
      : []

    // Acknowledged: the only thing left to offer is the way back from a mis-click.
    if (row.dismissedAt) return [{ id: 'restore', label: t('activity.restore'), kind: 'run' }, ...open]

    if (row.phase === 'failed') {
      return [...open, { id: 'dismiss', label: t('activity.run.dismiss'), kind: 'run' }]
    }
    return open.length ? open : undefined
  }

  function toTask(row: ActivityRunRow & { kind: HandledKind }): ActivityTask {
    const meta = KIND_MAP[row.kind]
    return {
      // Namespaced by the FEED kind, not the panel kind: two families could otherwise collide on a
      // shared uuid, and the id is what dismissal and the action handlers key on.
      id: `${row.kind}:${row.id}`,
      kind: meta.kind,
      title: titleFor(row),
      detail: detailFor(row),
      state: toState(row),
      badge: row.phase === 'failed'
        ? { text: t(row.kind === 'tool-run' ? 'activity.tool.badgeFailed' : 'activity.run.badgeFailed'), tone: 'bad' }
        : undefined,
      actions: actionsFor(row),
      startedAt: row.startedAt ? new Date(row.startedAt).getTime() : Date.now(),
      settledAt: row.completedAt ? new Date(row.completedAt).getTime() : undefined,
      dismissed: Boolean(row.dismissedAt),
    }
  }

  /**
   * Republish all four kinds from `rows`.
   *
   * Called from the poll tick and from every local mutation, imperatively rather than from a
   * watcher — the mutations are all in this file and all explicit, the same argument
   * `workflowRuns.publish()` makes.
   */
  function publish() {
    for (const feedKind of Object.keys(KIND_MAP) as HandledKind[]) {
      const mine = rows.value.filter((r): r is ActivityRunRow & { kind: HandledKind } => r.kind === feedKind)
      activity.replaceKind(
        KIND_MAP[feedKind].kind,
        mine.map(toTask),
        Object.fromEntries(mine.flatMap(r => [
          [`${r.kind}:${r.id}:dismiss`, () => dismissRow(r.kind, r.id)],
          [`${r.kind}:${r.id}:restore`, () => restoreRow(r.kind, r.id)],
        ])),
      )
    }
  }

  /**
   * Acknowledge one row, durably.
   *
   * The local removal is optimistic and the POST is fire-and-forget for the same reason it is in the
   * other two producers: worst case the row comes back on the next tick, which is a visible and
   * recoverable outcome, whereas awaiting it would leave the row sitting under the user's click.
   * The feed kind is handed straight to the endpoint — `ActivityDismissKind` names these four
   * identically to `ActivityRunKind` precisely so no producer has to translate.
   */
  function dismissRow(kind: HandledKind, id: string) {
    rows.value = rows.value.filter(r => !(r.kind === kind && r.id === id))
    activity.dismiss(`${kind}:${id}`)
    publish()
    apiFetch('/api/activity/dismiss', { method: 'POST', body: { kind, id } })
      .catch(() => { /* The next tick reports the real state. */ })
  }

  /** Undo a dismissal, from the "mostrar descartados" list. */
  function restoreRow(kind: HandledKind, id: string) {
    const row = rows.value.find(r => r.kind === kind && r.id === id)
    if (row) row.dismissedAt = null
    publish()
    apiFetch('/api/activity/dismiss', { method: 'POST', body: { kind, id, undo: true } })
      .catch(() => { /* The next tick reports the real state. */ })
  }

  /**
   * Drop acknowledged rows when "mostrar descartados" goes off.
   *
   * They simply stop arriving in the feed from then on, and reconciliation only removes what the
   * server contradicts — so without this they would stay on screen until a reload.
   */
  function purgeDismissed() {
    rows.value = rows.value.filter(r => !r.dismissedAt)
    publish()
  }

  /** Runs whose usage-limit block this session already recorded as the `usage` item. */
  const reportedBlocks = new Set<string>()

  /** Called by the shared feed each tick with the whole `runs` array. */
  function applyRuns(next: ActivityRunRow[]) {
    const handled = next.filter(isHandled)
    // A run the gateway refused is the ORGANIZATION's limit, not this run broken (MAR-1009): it is
    // the `usage` item `useUsageLimit` keeps, not one more generic "No se pudo completar" — which is
    // what the 10-02 dev re-test found for a refused sheet. The item only: the red banner is the run
    // stream's to raise, as it watches the run fail, and raising it again from here would reopen a
    // banner the user just closed. Once per run, so the row keeps its place across ticks.
    for (const row of handled) {
      const block = usageLimitOfRun(row)
      if (!block || row.dismissedAt || reportedBlocks.has(row.id)) continue
      reportedBlocks.add(row.id)
      recordUsageLimitActivity(block)
    }
    rows.value = handled.filter(r => !usageLimitOfRun(r))
    publish()
  }

  /** Anything still in flight — the feed uses it to decide whether to keep polling. */
  const pending = computed(() =>
    rows.value.some(r => r.phase === 'queued' || r.phase === 'running'))

  let requestPoll: (() => void) | null = null
  /** The shared feed registers here. One subscriber; a second would mean two timers again. */
  function onPollRequested(fn: () => void) {
    requestPoll = fn
  }

  /**
   * Ask the shared feed for a tick right now — the same contract `documentsIndexing.ensurePoll()`
   * has, and the replacement for `toolRuns.track()` (#70).
   *
   * A HANDLER, called from a user gesture (opening the panel) or from the code that just created
   * work (starting a conversion), never from a watcher. It matters for tool runs specifically
   * because this store has no optimistic row to insert: a conversion started in this tab, or in
   * another one, is only known once the feed has read it back.
   */
  function ensurePoll() {
    if (!import.meta.client) return
    requestPoll?.()
  }

  return { rows, pending, applyRuns, dismissRow, restoreRow, purgeDismissed, ensurePoll, onPollRequested }
})
